import { getPropClassesFromInstance } from 'lib/getPropClasses';
import cx from 'classnames';
import Layer from 'components/Layer';
import PropTypes from 'prop-types';
import * as React from 'react';

import './styles/Tooltip.scss';

/**
 * ## A [Layer](/internal/components/Layer) that surfaces contextual informational text content to the user.
 *
 *
 * # Implementation
 *
 * Directly or via props using
 * [tooltipable lib](https://github.com/remind101/r101-frontend/blob/master/src/lib/tooltipable.js).
 *
 *
 * # Placement
 *
 * ```example
 * <Buttons>
 *   <LayerTrigger
 *     layer={<Tooltip>Remind</Tooltip>}
 *     placement="top">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     layer={<Tooltip>Remind</Tooltip>}
 *     placement="right">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     layer={<Tooltip>Remind</Tooltip>}
 *     placement="bottom">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     layer={<Tooltip>Remind</Tooltip>}
 *     placement="left">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 * </Buttons>
 * ```
 *
 * # Alignment
 *
 * ```example
 * <Buttons>
 *   <LayerTrigger
 *     alignment="left"
 *     layer={<Tooltip>Top left</Tooltip>}
 *     placement="top">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     alignment="middle"
 *     layer={<Tooltip>Top middle</Tooltip>}
 *     placement="top">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     alignment="right"
 *     layer={<Tooltip>Top right</Tooltip>}
 *     placement="top">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     alignment="top"
 *     layer={<Tooltip>Right top</Tooltip>}
 *     placement="right">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     alignment="middle"
 *     layer={<Tooltip>Right middle</Tooltip>}
 *     placement="right">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 *   <LayerTrigger
 *     alignment="bottom"
 *     layer={<Tooltip>Right bottom</Tooltip>}
 *     placement="right">
 *     <Glyph name="remind-icon" size="xlarge" />
 *   </LayerTrigger>
 * </Buttons>
 * ```
 *
 * ```example
 * <LayerTrigger
 *   header="Header"
 *   layer={<Tooltip>Remind</Tooltip>}>
 *   <Glyph name="remind-icon" size="xlarge" />
 *
 * ```example
 * <LayerTrigger
 *   color="atlas"
 *   layer={<Tooltip>atlas</Tooltip>}>
 *   <Glyph name="remind-icon" size="xlarge" />
 * ```
 *
 * # tooltipable
 *
 * ```example
 * <Buttons>
 *   <Button
 *     tooltip="Top middle"
 *     tooltipAlignment="middle"
 *     tooltipPlacement="top">
 *     Button
 *   </Button>
 *   <Link
 *     tooltip="Top middle"
 *     tooltipAlignment="middle"
 *     tooltipPlacement="top">
 *     Link
 *   </Link>
 * </Buttons>
 * ```
 *
 *
 * # TODO
 * * Fix `alignment` with side `placement` positioning
 * * Consider enforcing `focus`-able child
 */

class Tooltip extends React.Component<any> {
  static propTypes = {
    ...Layer.propTypes,
    color: PropTypes.oneOf(['default', 'apple', 'elmer', 'atlas', 'pennant', 'whiteout']),
    header: PropTypes.string,
    type: PropTypes.oneOf(['normal', 'nux']),
  };

  static propClasses = {
    color: {
      apple: 'Tooltip--apple',
      elmer: 'Tooltip--elmer',
      atlas: 'Tooltip--atlas',
      pennant: 'Tooltip--pennant',
      whiteout: 'Tooltip--whiteout',
    },
    type: {
      nux: 'Tooltip--nux',
    },
  };

  static defaultProps = {
    type: 'normal',
  };

  render() {
    const { children, className, header, ...props } = this.props;
    const anyCast: any = this;
    return (
      <Layer {...props} className={cx('Tooltip', getPropClassesFromInstance(anyCast), className)}>
        {header && <div className="Tooltip-header">{header}</div>}
        {children}
      </Layer>
    );
  }
}

export default Tooltip;
